import React, { useState, useEffect } from "react";
import { Info, Plus, X } from "lucide-react";
import UserProfileSelector from "./UserProfileSelector.tsx";
import { 
  User, 
  Profile, 
  AssociationCreateTabProps 
} from "../../ui/types";

const LocalInfoBox = ({ children, isDark }: { children: React.ReactNode; isDark: boolean }) => {
  return (
    <div className={`flex items-start p-2 rounded-lg mb-4 border border-green-500 ${
      isDark ? "bg-green-900/10" : "bg-green-50"
    }`}>
      {children}
    </div>
  );
};

const AssociationCreateTab = ({
  users,
  profiles,
  selectedUsers,
  selectedProfiles,
  isMobile,
  isDarkMode,
  hasExistingAssociation,
  isUserAlreadyAssociated,
  getAssociatedProfile,
  onUserSelect,
  onProfileSelect,
  onClearSelections,
  onCreateAssociation
}: AssociationCreateTabProps) => {
  
  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);
  
  return (
    <div className="mt-4">
      <LocalInfoBox isDark={isDarkMode}>
        <Info 
          size={isMobile ? 22 : 40} 
          className={`mr-2 ${isDarkMode ? "text-green-400" : "text-green-600"}`} 
        />
        <p className={`text-sm ${isDarkMode ? "text-green-100" : "text-green-800"}`}>
          {textConfig.ASSOCIATION_CREATE_INFO_TEXT}
        </p>
      </LocalInfoBox>

      <h3 className="text-xl font-medium mb-4 text-[#2ECC71]">
        {textConfig.ASSOCIATION_CREATE_TITLE}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                 <UserProfileSelector
           title={textConfig.ASSOCIATION_CREATE_USER_LABEL}
           items={users}
           selectedItems={selectedUsers}
           itemKey="username"
           onSelect={(user) => onUserSelect(user as User | null)}
           onClear={() => onUserSelect(null)}
           isDarkMode={isDarkMode}
           isUserSelector={true}
           isUserAlreadyAssociated={isUserAlreadyAssociated}
           getAssociatedProfile={getAssociatedProfile}
         />

        <UserProfileSelector
          title={textConfig.ASSOCIATION_CREATE_PROFILE_LABEL}
          items={profiles}
          selectedItems={selectedProfiles}
          itemKey="profile_id"
          onSelect={(profile) => onProfileSelect(profile as Profile | null)}
          onClear={() => onProfileSelect(null)}
          isDarkMode={isDarkMode}
        />
      </div>

      <div className="flex justify-end space-x-3">
        {(selectedUsers.length > 0 || selectedProfiles.length > 0) && (
          <button
            onClick={onClearSelections}
            className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-md transition-colors flex items-center gap-2"
          >
            <X size={18} />
            {textConfig.ASSOCIATION_CREATE_CLEAR_BUTTON}
          </button>
        )}
        
        {selectedUsers.length > 0 && selectedProfiles.length > 0 && (
          <button
            onClick={onCreateAssociation}
            className={`${
              hasExistingAssociation 
                ? "bg-gray-500 cursor-not-allowed" 
                : "bg-[#2ECC71] hover:bg-[#28B463] cursor-pointer"
            } text-white py-2 px-4 rounded-md transition-colors flex items-center gap-2`}
            disabled={hasExistingAssociation}
          >
            {hasExistingAssociation ? (
              <>{textConfig.ASSOCIATION_CREATE_EXISTING_ASSOCIATION}</>
            ) : (
              <>
                <Plus size={18} />
                {textConfig.ASSOCIATION_CREATE_BUTTON}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default AssociationCreateTab;